import AuthService from "./AuthService";

/**
 * WebSocket service for real-time notifications using Socket.IO
 */
type EventCallback = (data: unknown) => void;

interface SocketClient {
	id?: string;
	on(eventName: string, callback: (...args: unknown[]) => void): void;
	onAny(callback: (eventName: string, ...args: unknown[]) => void): void;
	emit(eventName: string, ...args: unknown[]): void;
	disconnect(): void;
}

interface SocketFactory {
	(
		url: string,
		options: {
			transports: string[];
			withCredentials?: boolean;
			reconnection?: boolean;
			auth: {
				strategy: string;
				accessToken: string;
			};
		},
	): SocketClient;
}

interface NotificationPayload {
	type: string;
	data: unknown;
	timestamp: string;
}

class WebSocketServiceClass {
	private socket: SocketClient | null = null;
	private connected = false;
	private listeners: Map<string, Set<EventCallback>> = new Map();
	private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
	private reconnectAttempts = 0;

	private static readonly RECONNECT_BASE_MS = 5_000;
	private static readonly RECONNECT_MAX_MS = 300_000;

	/**
	 * Connect to the WebSocket server and authenticate
	 */
	async connect(): Promise<void> {
		if (this.connected || this.socket) {
			console.log("[WebSocket] Already connected or connecting");
			return;
		}

		const accessToken = AuthService.getAccessToken();
		if (!accessToken) {
			console.warn("[WebSocket] No access token, skipping connection");
			return;
		}

		try {
			// Dynamically import socket.io-client from CDN
			const io = await this.loadSocketIO();
			if (!io) {
				console.error("[WebSocket] Failed to load socket.io-client");
				return;
			}

			const socketUrl = this.resolveSocketUrl();
			console.log("[WebSocket] Connecting to:", socketUrl);

			// Connect to the backend Socket.IO server
			// Disable built-in reconnection; we handle it ourselves with token refresh.
			const socket = io(socketUrl, {
				transports: ["polling", "websocket"],
				withCredentials: true,
				reconnection: false,
				auth: {
					strategy: "jwt",
					accessToken: accessToken,
				},
			});

			this.socket = socket;

			socket.on("connect", () => {
				console.log("[WebSocket] Connected, socket id:", socket.id);
				this.connected = true;

				// Always read the current token from storage (may have been refreshed)
				const currentToken = AuthService.getAccessToken();
				if (!currentToken) {
					console.warn("[WebSocket] No access token at connect time, skipping auth");
					return;
				}

				// Authenticate the socket connection
				socket.emit(
					"create",
					"authentication",
					{
						strategy: "jwt",
						accessToken: currentToken,
					},
					(error: unknown, result: unknown) => {
						if (error) {
							// The only case where the token is actually implicated.
							console.error("[WebSocket] Authentication failed:", error);
							this.scheduleReconnect({ refreshToken: true });
						} else {
							console.log("[WebSocket] Authenticated successfully:", result);
							// Fully established - reset the backoff ladder.
							this.reconnectAttempts = 0;
						}
					},
				);
			});

			socket.on("disconnect", (reason: unknown) => {
				console.log(
					"[WebSocket] Disconnected:",
					typeof reason === "string" ? reason : reason,
				);
				this.connected = false;
				this.scheduleReconnect();
			});

			socket.on("connect_error", (error: unknown) => {
				const message = error instanceof Error ? error.message : String(error);
				console.error("[WebSocket] Connection error:", message);
				this.connected = false;
				this.scheduleReconnect();
			});

			// Listen for all messages/notifications
			socket.onAny((eventName: string, ...args: unknown[]) => {
				console.log("[WebSocket] Event received:", eventName, args);
				this.handleEvent(eventName, args[0]);
			});
		} catch (error) {
			console.error("[WebSocket] Failed to connect:", error);
		}
	}

	private resolveSocketUrl(): string {
		const { protocol, hostname, port, origin } = window.location;

		// In UI5 local development the app runs on :8080 while Feathers + Socket.IO run on :3030.
		if (port === "8080") {
			return `${protocol}//${hostname}:3030`;
		}

		return origin;
	}

	/**
	 * Load Socket.IO client library
	 */
	private async loadSocketIO(): Promise<SocketFactory | null> {
		// Check if already loaded
		if ((window as unknown as { io?: SocketFactory }).io) {
			return (window as unknown as { io: SocketFactory }).io;
		}

		return new Promise((resolve) => {
			const script = document.createElement("script");
			script.src = "https://cdn.socket.io/4.7.4/socket.io.min.js";
			script.onload = () => {
				const io = (window as unknown as { io?: SocketFactory }).io;
				resolve(io || null);
			};
			script.onerror = () => {
				console.error("[WebSocket] Failed to load socket.io-client script");
				resolve(null);
			};
			document.head.appendChild(script);
		});
	}

	/**
	 * Schedule a reconnection attempt using jittered exponential backoff.
	 *
	 * Only pass `refreshToken` when the socket was rejected for *authentication*.
	 * A network-level drop says nothing about token validity, and refreshing on
	 * every attempt burns the /api/auth/refresh rate limit (shared per-IP), which
	 * surfaces as a 429 and - before this - logged the user out entirely.
	 */
	private scheduleReconnect(options?: { refreshToken?: boolean }): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
		}

		const backoff = Math.min(
			WebSocketServiceClass.RECONNECT_MAX_MS,
			WebSocketServiceClass.RECONNECT_BASE_MS * 2 ** this.reconnectAttempts,
		);
		// Full jitter: without it, every tab and every user retries in lockstep
		// after a backend restart and re-triggers the limiter as one herd.
		const delay = Math.round(backoff * (0.5 + Math.random() * 0.5));
		this.reconnectAttempts++;

		console.log(
			`[WebSocket] Reconnect attempt ${this.reconnectAttempts} in ${delay}ms`,
		);

		this.reconnectTimer = setTimeout(async () => {
			if (!AuthService.isAuthenticated() || this.connected) {
				return;
			}

			// Clear the stale socket so connect() can proceed
			if (this.socket) {
				this.socket.disconnect();
				this.socket = null;
			}

			if (options?.refreshToken) {
				try {
					await AuthService.refreshTokens();
				} catch (error) {
					// A dead token already cleared the session in AuthService; a
					// transient failure (429/5xx) leaves it intact, so keep backing
					// off and try again rather than giving up on the socket.
					console.warn("[WebSocket] Token refresh failed:", error);
					if (!AuthService.isAuthenticated()) {
						return;
					}
					this.scheduleReconnect({ refreshToken: true });
					return;
				}
			}

			void this.connect();
		}, delay);
	}

	/**
	 * Handle incoming events and dispatch to listeners
	 */
	private handleEvent(eventName: string, data: unknown): void {
		// Handle Feathers channel messages (they come as plain objects)
		const payload = data as NotificationPayload;
		if (payload && typeof payload === "object" && payload.type) {
			const eventType = payload.type;
			console.log("[WebSocket] Notification:", eventType, payload.data);

			const callbacks = this.listeners.get(eventType);
			if (callbacks) {
				callbacks.forEach((cb) => cb(payload));
			}

			// Also notify generic listeners
			const allCallbacks = this.listeners.get("*");
			if (allCallbacks) {
				allCallbacks.forEach((cb) => cb(payload));
			}
		}

		// Dispatch raw event to listeners
		const callbacks = this.listeners.get(eventName);
		if (callbacks) {
			callbacks.forEach((cb) => cb(data));
		}
	}

	/**
	 * Subscribe to a specific event type
	 */
	on(eventType: string, callback: EventCallback): () => void {
		if (!this.listeners.has(eventType)) {
			this.listeners.set(eventType, new Set());
		}
		this.listeners.get(eventType)!.add(callback);

		// Return unsubscribe function
		return () => {
			this.listeners.get(eventType)?.delete(callback);
		};
	}

	/**
	 * Disconnect from the WebSocket server
	 */
	disconnect(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}

		// Deliberate teardown (e.g. logout) - the next session should start at the
		// base delay rather than inheriting a long backoff from the old one.
		this.reconnectAttempts = 0;

		if (this.socket) {
			this.socket.disconnect();
			this.socket = null;
			this.connected = false;
		}

		console.log("[WebSocket] Disconnected");
	}

	/**
	 * Check if connected
	 */
	isConnected(): boolean {
		return this.connected;
	}
}

const WebSocketService = new WebSocketServiceClass();
export default WebSocketService;
